import { ColumnType } from "antd/es/table/interface";
import { PermissionEnum } from "types/enums.ts";
import { DrawerId, Identifiable, NonIdentifiable, TableId } from "types/models.ts";

import { FilterType } from "./filter";

import PageRequest = App.Http.Requests.PageRequest;
import FilterRequest = App.Http.Requests.FilterRequest;

// Granularity of the table's top-level date-range filter (TimeFilter/DateRangePicker).
// The user picks down to this unit; anything finer is clamped automatically — 0 for the
// start of the range, the max value for the end — same as "date" already does for time-of-day.
// "date" (default): day only, time clamped to 00:00:00 / 23:59:59.
// "hour": date + hour, minute/second clamped to 00 / 59:59.
// "minute": date + hour + minute, second clamped to 00 / 59.
// "second": full precision, nothing clamped.
export type DateFilterMode = "date" | "hour" | "minute" | "second";

export interface TableState<T extends Identifiable | NonIdentifiable> {
    id: TableId;
    editingRowId: string | null;
    config: TableLocalStorageConfig<T>; //config saved in local storage (visible columns and order of columns
    columns: TableColumnType<T>[] | null; //columns modified by order and visibility
    originColumns: TableColumnType<T>[] | null; //original columns config
    page: TablePageRequest; //pagination
    filter: FilterRequest;
    showOptionsFilter: boolean;
    showColumnsVisibility: boolean;
    initialized: boolean;
    timeFilterEnabled: boolean; //whether the table renders the TimeFilter — gates the global timeFilterAtom from leaking into this table's filters
}

export interface TablePageRequest extends PageRequest {
    size?: PageSize;
}

export enum PageSize {
    Five = 5,
    Ten = 10,
    Thirty = 30,
    OneHundred = 100,
    TwoHundred = 200,
    FiveHundred = 500,
    OneThousand = 1000,
}

export interface TableColumnType<T> extends ColumnType<T> {
    dataIndex: string | string[] | number;
    editable?: boolean;
    filterable?: boolean;
    filterType?: FilterType;
    filterOptions?: { label: string; value: string | number | boolean }[];
    filterName?: string;
    filterIncludeNull?: boolean;
    filterNullOptionLabel?: string;
    sortName?: string;
    inputType?: string;
    visible?: boolean;
    order?: number;
    //showSorterTooltip?: boolean | { target: string };
    defaultSorter?: boolean;
    defaultSortOrder?: "ascend" | "descend";
}

export interface QuickFilterOption {
    label: string;
    value: string;
}

export interface TableLocalStorageConfig<T> {
    columnsConfig: TableColumnType<T>[];
}

export interface TableUrlParameters {
    page?: PageRequest;
    filter?: FilterRequest;
    sort?: string;
}

// Define action type
type ActionType = "detail" | "drawer" | "edit" | "delete" | "custom" | "customButton";

interface DrawerAction<T> {
    to?: (record: T) => string | string;
    search?: (record: T) => { [x: string]: string };
    drawerId: DrawerId;
    disabled?: boolean;
}

// Define action interface
export interface TableAction<T extends Identifiable | NonIdentifiable> {
    type: ActionType;
    label?: string | React.ReactNode | ((record: T) => React.ReactNode);
    icon?: React.ReactNode;
    onClick?: (record: T) => void;
    link?: string;
    addToLink?: string;
    drawer?: DrawerAction<T>;
    visible?: (record: T) => boolean;
    permission?: PermissionEnum;
}
